import React, { useEffect, lazy } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScrollReveal } from '../components/ScrollReveal';
import { useSEO } from '../utils/seo';
import { blogPosts } from '../data/blogPosts';
export const BlogPost = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);
  useEffect(() => {
    if (!post) {
      navigate('/blog', {
        replace: true
      });
    }
  }, [post, navigate]);
  useSEO({
    title: post ? `${post.title} | Blog Dispel Your Tattoo` : 'Blog | Dispel Your Tattoo',
    description: post?.excerpt || '',
    canonical: `https://www.dispelyourtattoo.ch/blog/${slug}`
  });
  if (!post) return null;
  // Get 2 random other posts for the "Other articles" section
  const otherPosts = blogPosts.filter(p => p.slug !== slug).sort(() => 0.5 - Math.random()).slice(0, 2);
  return <main className="pt-24 pb-0 bg-brand-dark min-h-screen" data-id="element-324">
      {/* Article Header */}
      <article data-id="element-325">
        <header className="pt-12 md:pt-16 lg:pt-24 pb-8 md:pb-12 px-6 md:px-12 max-w-[900px] mx-auto text-center" data-id="element-326">
          <ScrollReveal data-id="element-327">
            <Link to="/blog" className="inline-flex items-center space-x-2 text-brand-gray hover:text-brand-light transition-colors mb-8 md:mb-12 uppercase tracking-widest text-[10px] md:text-xs font-bold" data-id="element-328">
              <ArrowLeft size={14} data-id="element-329" />
              <span data-id="element-330">Retour au blog</span>
            </Link>
          </ScrollReveal>

          <ScrollReveal delay={0.1} data-id="element-331">
            <div className="flex items-center justify-center space-x-3 md:space-x-6 text-xs md:text-sm text-brand-gray mb-6 md:mb-8 font-light flex-wrap gap-y-3" data-id="element-332">
              <div className="flex items-center space-x-1.5 md:space-x-2" data-id="element-333">
                <Calendar size={14} className="text-brand-light/50 md:w-4 md:h-4" data-id="element-334" />
                <span data-id="element-335">{post.date}</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-brand-border" data-id="element-336" />
              <div className="flex items-center space-x-1.5 md:space-x-2" data-id="element-337">
                <Clock size={14} className="text-brand-light/50 md:w-4 md:h-4" data-id="element-338" />
                <span data-id="element-339">{post.readTime}</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-brand-border" data-id="element-340" />
              <div className="flex items-center space-x-1.5 md:space-x-2" data-id="element-341">
                <User size={14} className="text-brand-light/50 md:w-4 md:h-4" data-id="element-342" />
                <span data-id="element-343">Par {post.author}</span>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-344">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-medium text-brand-light mb-6 md:mb-8 tracking-tight text-balance leading-[1.2]" data-id="element-345">
              {post.title}
            </h1>
          </ScrollReveal>
        </header>

        {/* Featured Image */}
        <ScrollReveal delay={0.3} data-id="element-346">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12 mb-12 md:mb-16 lg:mb-24" data-id="element-347">
            <div className="aspect-[16/9] md:aspect-[21/9] lg:aspect-[2.5/1] rounded-2xl md:rounded-3xl overflow-hidden border border-brand-border/30" data-id="element-348">
              <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover grayscale-[20%]" data-id="element-349" />
            </div>
          </div>
        </ScrollReveal>

        {/* Article Content */}
        <div className="max-w-prose mx-auto px-6 md:px-12 pb-16 md:pb-24" data-id="element-350">
          <div className="prose prose-invert prose-base md:prose-lg lg:prose-xl prose-p:text-brand-gray prose-p:font-light prose-p:leading-relaxed prose-headings:text-brand-light prose-headings:font-bold prose-headings:tracking-tight prose-a:text-brand-light prose-a:no-underline hover:prose-a:text-brand-gray prose-img:rounded-xl md:prose-img:rounded-2xl prose-img:border prose-img:border-brand-border/30 max-w-none" data-id="element-351">
            {post.content.map((block, index) => {
            if (block.type === 'paragraph') {
              return <ScrollReveal key={index} delay={0.1} data-id="element-352">
                    <p className="mb-6 md:mb-8" data-id="element-353">{block.content}</p>
                  </ScrollReveal>;
            }
            if (block.type === 'heading') {
              return <ScrollReveal key={index} delay={0.1} data-id="element-354">
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-medium tracking-tight mt-12 md:mt-16 mb-6 md:mb-8" data-id="element-355">
                      {block.content}
                    </h2>
                  </ScrollReveal>;
            }
            if (block.type === 'image') {
              return <ScrollReveal key={index} delay={0.1} data-id="element-356">
                    <figure className="my-8 md:my-12" data-id="element-357">
                      <img src={block.content} alt={block.alt || ''} className="w-full h-auto object-cover grayscale-[20%]" loading="lazy" data-id="element-358" />
                      {block.alt && <figcaption className="text-center text-xs md:text-sm text-brand-gray/60 mt-3 md:mt-4 font-light" data-id="element-359">
                          {block.alt}
                        </figcaption>}
                    </figure>
                  </ScrollReveal>;
            }
            return null;
          })}
          </div>

          {/* Share / Author Box */}
          <ScrollReveal data-id="element-360">
            <div className="mt-16 md:mt-24 pt-8 md:pt-12 border-t border-brand-border/30 flex items-center justify-between" data-id="element-361">
              <div className="flex items-center space-x-4" data-id="element-362">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-darker border border-brand-border flex items-center justify-center" data-id="element-363">
                  <User size={18} className="text-brand-light md:w-5 md:h-5" data-id="element-364" />
                </div>
                <div data-id="element-365">
                  <p className="text-brand-light font-bold text-xs md:text-sm uppercase tracking-wider" data-id="element-366">
                    {post.author}
                  </p>
                  <p className="text-brand-gray text-xs md:text-sm font-light" data-id="element-367">
                    Dispel Your Tattoo
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </article>

      {/* Other Articles */}
      <section className="py-16 md:py-24 bg-brand-darker border-t border-brand-border/30" data-id="element-368">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12" data-id="element-369">
          <ScrollReveal data-id="element-370">
            <div className="flex items-end justify-between mb-8 md:mb-12" data-id="element-371">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-brand-light tracking-tight" data-id="element-372">
                À lire aussi
              </h2>
              <Link to="/blog" className="hidden md:inline-flex items-center space-x-2 text-brand-light font-bold uppercase tracking-wider text-sm hover:text-brand-gray transition-colors" data-id="element-373">
                <span data-id="element-374">Voir tout</span>
                <ArrowRight size={16} data-id="element-375" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-376">
            {otherPosts.map((otherPost, index) => <ScrollReveal key={otherPost.slug} delay={index * 0.1} data-id="element-377">
                <Link to={`/blog/${otherPost.slug}`} className="group block bg-brand-dark rounded-2xl overflow-hidden border border-brand-border/30 h-full flex flex-col" data-id="element-378">
                  <div className="relative aspect-[16/9] overflow-hidden" data-id="element-379">
                    <motion.img whileHover={{
                  scale: 1.05
                }} transition={{
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1]
                }} src={otherPost.featuredImage} alt={otherPost.title} className="w-full h-full object-cover grayscale-[20%]" loading="lazy" data-id="element-380" />
                  </div>
                  <div className="p-6 md:p-8 flex-grow flex flex-col" data-id="element-381">
                    <div className="flex items-center space-x-3 md:space-x-4 text-[10px] md:text-xs text-brand-gray mb-3 md:mb-4 font-light" data-id="element-382">
                      <div className="flex items-center space-x-1.5" data-id="element-383">
                        <Calendar size={12} className="md:w-3.5 md:h-3.5" data-id="element-384" />
                        <span data-id="element-385">{otherPost.date}</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-brand-border" data-id="element-386" />
                      <div className="flex items-center space-x-1.5" data-id="element-387">
                        <Clock size={12} className="md:w-3.5 md:h-3.5" data-id="element-388" />
                        <span data-id="element-389">{otherPost.readTime}</span>
                      </div>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-brand-light mb-4 tracking-tight group-hover:text-brand-gray transition-colors duration-300 text-balance" data-id="element-390">
                      {otherPost.title}
                    </h3>
                    <div className="mt-auto pt-4 inline-flex items-center space-x-2 text-brand-light font-bold uppercase tracking-wider text-[10px] md:text-xs group-hover:translate-x-2 transition-transform duration-300" data-id="element-391">
                      <span data-id="element-392">Lire l'article</span>
                      <ArrowRight size={14} data-id="element-393" />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>)}
          </div>

          <div className="mt-10 md:mt-12 text-center md:hidden" data-id="element-394">
            <Link to="/blog" className="inline-flex items-center space-x-2 text-brand-light font-bold uppercase tracking-wider text-xs hover:text-brand-gray transition-colors" data-id="element-395">
              <span data-id="element-396">Voir tout le blog</span>
              <ArrowRight size={14} data-id="element-397" />
            </Link>
          </div>
        </div>
      </section>
    </main>;
};